import { t as uiText } from '../i18n';
import React, { useState, useRef, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { useUserImage } from '../hooks/useUserImage';
import { updateUserProfile } from '../features/auth/updateUserProfile';
import { getUserImagePath } from '../features/auth/userImageUtils';
import ImageCropModal from './ImageCropModal';

interface UserSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserSettingsModal: React.FC<UserSettingsModalProps> = ({ isOpen, onClose }) => {
  const [user] = useAuthState(auth as any);
  const [displayName, setDisplayName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [avatarImgError, setAvatarImgError] = useState(false);
  const [previewImgError, setPreviewImgError] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [notificationEmail, setNotificationEmail] = useState('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const isValidEmail = (email: string): boolean => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  const emailError = notificationEmail.trim() !== '' && !isValidEmail(notificationEmail.trim())
    ? uiText("Email address format is invalid")
    : null;
  const canUpdate = !isUpdating && !!displayName.trim() && !emailError;

  const { 
    imageUrl, 
    imageData, 
    isLoading: imageLoading, 
    error: imageError, 
    hasImage, 
    reload: reloadImage 
  } = useUserImage();

  // モーダルが開かれた時にユーザー情報を初期化
  useEffect(() => {
    if (isOpen && user) {
      setDisplayName(user.displayName || '');
      setSelectedFile(null);
      setPreviewUrl(null);
      setAvatarImgError(false);
      setPreviewImgError(false);
      setError(null);
      setUpdateProgress(0);

      // Firestoreから通知メールアドレスを読み込み
      (async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const data = userDoc.exists() ? userDoc.data() : null;
          setNotificationEmail(data?.notificationEmail || '');
        } catch {
          setNotificationEmail('');
        }
      })();
    }
  }, [isOpen, user]);

  // 表示する画像ソースが変わったらエラー状態をリセット
  useEffect(() => {
    setAvatarImgError(false);
  }, [user?.uid, imageUrl, imageData]);

  useEffect(() => {
    setPreviewImgError(false);
  }, [previewUrl]);

  // モーダルの外側をクリックした時の処理
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // クロップモーダルが表示されている時は外側クリック処理を無効化
      if (showCropModal) return;
      
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // モーダル表示時にbodyのスクロールを無効化
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, showCropModal]);

  // ESCキーでモーダルを閉じる
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // クロップモーダルが表示されている時はESCキー処理を無効化
      if (showCropModal) return;
      
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, showCropModal]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImageFile(file);
      setShowCropModal(true);
    }
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpdate = async () => {
    if (!user) return;

    setIsUpdating(true);
    setError(null);
    setUpdateProgress(0);

    try {
      // 現在の画像パスを取得
      const currentPhotoPath = await getUserImagePath(user.uid);

      const result = await updateUserProfile({
        userId: user.uid,
        displayName: displayName.trim(),
        imageFile: selectedFile,
        currentPhotoPath: currentPhotoPath,
        notificationEmail: notificationEmail.trim() || null,
        onProgress: setUpdateProgress
      });

      // 画像を再読み込みして最新の状態を反映
      reloadImage();

      // 成功メッセージ
      alert(uiText("User information updated"));
      onClose();
    } catch (error) {
      console.error('❌ 更新エラー:', error);
      const errorMessage = error instanceof Error ? error.message : uiText("Failed to update");
      setError(errorMessage);
    } finally {
      setIsUpdating(false);
      setUpdateProgress(0);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
      }}
    >
      <div
        ref={modalRef}
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }}
      >
        {/* タイトル */}
        <h2
          style={{
            margin: '0 0 24px 0',
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#333',
            textAlign: 'center',
          }}
        >
          {uiText("User settings")}
        </h2>

        {/* アイコン設定 */}
        <div style={{ marginBottom: '24px' }}>
          <h3
            style={{
              margin: '0 0 12px 0',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#333',
            }}
          >
            {uiText("Icon settings")} <span style={{ color: '#dc3545' }}>*</span>
          </h3>
          
          {/* プレビュー */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: '#f8f9fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                border: '2px solid #e9ecef',
              }}
            >
              {(() => {
                const initial =
                  user?.displayName?.trim().charAt(0) ||
                  user?.email?.trim().charAt(0) ||
                  'U';
                const hasCurrentImage = !!(imageData || imageUrl);

                const shouldShowPreviewImage = !!previewUrl && !previewImgError;
                const shouldShowCurrentImage =
                  !previewUrl && hasCurrentImage && !imageLoading && !avatarImgError;

                return (
                  <>
                    {shouldShowPreviewImage ? (
                      <img
                        src={previewUrl || ''}
                        alt={uiText("Preview")}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={() => setPreviewImgError(true)}
                      />
                    ) : null}

                    {shouldShowCurrentImage ? (
                      <img
                        src={imageData || imageUrl || ''}
                        alt={uiText("Current icon")}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={() => setAvatarImgError(true)}
                      />
                    ) : null}

                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: shouldShowPreviewImage || shouldShowCurrentImage ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#6c757d',
                      }}
                    >
                      {initial}
                    </div>
                  </>
                );
              })()}
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#6c757d',
                }}
              >
                {user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333', marginBottom: '4px' }}>
                {uiText("Preview")}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {previewUrl ? uiText("New image") : (imageData || imageUrl) ? uiText("Image icon") : uiText("Letter icon")}
              </div>
            </div>
          </div>

          {/* ファイル選択 */}
          <div style={{ marginBottom: '12px' }}>
            <h4
              style={{
                margin: '0 0 8px 0',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#333',
              }}
            >
              {uiText("Image icon")}
            </h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--workspace-primary, #cfae91)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                }}
              >
                {uiText("Select file")}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <div
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#f8f9fa',
                  fontSize: '14px',
                  color: '#666',
                }}
              >
                {selectedFile ? selectedFile.name : uiText("None selected")}
              </div>
            </div>
          </div>

          {/* 画像削除ボタン */}
          {(previewUrl || (imageData || imageUrl)) && (
            <button
              onClick={handleRemoveImage}
              style={{
                padding: '6px 12px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                marginBottom: '8px',
              }}
            >
              {uiText("Delete image")}
            </button>
          )}

          {/* 説明文 */}
          <p
            style={{
              margin: '0',
              fontSize: '12px',
              color: '#666',
              lineHeight: '1.4',
            }}
          >
            {uiText("Select an image to use as your profile icon")}
          </p>
        </div>

        {/* アカウント名設定 */}
        <div style={{ marginBottom: '24px' }}>
          <h3
            style={{
              margin: '0 0 12px 0',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#333',
            }}
          >
            {uiText("Account name")} <span style={{ color: '#dc3545' }}>*</span>
          </h3>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={uiText("Enter an account name")}
              maxLength={48}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
            <div
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '12px',
                color: '#666',
              }}
            >
              {displayName.length}/48
            </div>
          </div>
        </div>

        {/* 通知メールアドレス設定 */}
        <div style={{ marginBottom: '24px' }}>
          <h3
            style={{
              margin: '0 0 12px 0',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#333',
            }}
          >
            {uiText("Notification email")}
          </h3>
          <input
            type="email"
            value={notificationEmail}
            onChange={(e) => setNotificationEmail(e.target.value)}
            placeholder={user?.email || uiText("Enter email address")}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${emailError ? '#dc3545' : '#ddd'}`,
              borderRadius: '4px',
              fontSize: '14px',
              boxSizing: 'border-box',
              marginBottom: emailError ? '4px' : '8px',
            }}
          />
          {emailError && (
            <p
              style={{
                margin: '0 0 4px 0',
                fontSize: '12px',
                color: '#dc3545',
                lineHeight: '1.4',
              }}
            >
              {emailError}
            </p>
          )}
          <p
            style={{
              margin: '0 0 4px 0',
              fontSize: '12px',
              color: '#666',
              lineHeight: '1.4',
            }}
          >
            {uiText("You can set the email address that receives notifications from notreu.")}
          </p>
          <p
            style={{
              margin: '0',
              fontSize: '12px',
              color: '#999',
              lineHeight: '1.4',
            }}
          >
            {uiText("If not set, the sign-in email (")}{user?.email || uiText("Not set")}{uiText("). It will be sent here.")}
          </p>
        </div>

        {/* エラーメッセージ */}
        {error && (
          <div
            style={{
              padding: '12px',
              backgroundColor: '#fff5f5',
              border: '1px solid #fed7d7',
              borderRadius: '4px',
              color: '#c53030',
              fontSize: '14px',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        {/* 進捗表示 */}
        {isUpdating && updateProgress > 0 && (
          <div
            style={{
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '4px',
              }}
            >
              <span style={{ fontSize: '14px', color: '#666' }}>{uiText("Updating...")}</span>
              <span style={{ fontSize: '14px', color: '#666' }}>{updateProgress}%</span>
            </div>
            <div
              style={{
                width: '100%',
                height: '4px',
                backgroundColor: '#e2e8f0',
                borderRadius: '2px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${updateProgress}%`,
                  height: '100%',
                  backgroundColor: 'var(--workspace-primary, #cfae91)',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        )}

        {/* アクションボタン */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            onClick={handleUpdate}
            disabled={!canUpdate}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: canUpdate ? 'var(--workspace-primary, #cfae91)' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: canUpdate ? 'pointer' : 'not-allowed',
              fontSize: '16px',
              fontWeight: 'bold',
            }}
          >
            {isUpdating ? uiText("Updating...") : uiText("Update")}
          </button>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: 'transparent',
              color: '#666',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              textAlign: 'center',
            }}
          >
            {uiText("← Back")}
          </button>
        </div>
      </div>

      {/* 画像クロップモーダル */}
      {showCropModal && selectedImageFile && (
        <ImageCropModal
          imageFile={selectedImageFile}
          cropType="circle"
          onCropComplete={(croppedFile, dataUrl) => {
            setSelectedFile(croppedFile);
            setPreviewUrl(dataUrl);
            setShowCropModal(false);
            setSelectedImageFile(null);
          }}
          onCancel={() => {
            setShowCropModal(false);
            setSelectedImageFile(null);
          }}
        />
      )}
    </div>
  );
};

export default UserSettingsModal;
